import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  get sectionId() {
    const container = this.closest('featured-product-information[id], .shopify-section');
    return container ? container.id : 'default';
  }

  // Use window-level storage for persistence across gallery replacements scoped by section
  get #lastFilterLabel() {
    return window[`MediaGallery_lastFilterLabel_${this.sectionId}`] || '';
  }
  set #lastFilterLabel(val) {
    window[`MediaGallery_lastFilterLabel_${this.sectionId}`] = val;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    document.addEventListener('background-color:changed', this.#handleBackgroundColorChange, { signal });
    document.addEventListener('custom-variant:media-match', this.#handleInstantVariantMatch, { signal });

    if (this.#lastFilterLabel || window[`MediaGallery_lastIsWithoutFrame_${this.sectionId}`] || window[`MediaGallery_lastFrameColor_${this.sectionId}`]) {
      this.#handleBackgroundColorChange({ detail: { sectionId: this.sectionId } });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles instant variant thumbnail switching locally before backend responds
   */
  #handleInstantVariantMatch = (event) => {
    const mediaId = event.detail.mediaId;
    if (!mediaId) return;

    // Search for slide/thumbnail wrapping this media id
    const slideWrap = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (slideWrap && slideWrap.style.display !== 'none' && this.slideshow && typeof this.slideshow.select === 'function') {
      const parent = slideWrap.parentElement;
      if (parent) {
        const slides = Array.from(parent.children);
        const idx = slides.indexOf(slideWrap);
        if (idx !== -1) {
          this.slideshow.select(idx, undefined, { animate: false });
        }
      }
    }
  };

  /**
   * Handles the 'background-color:changed' event to filter images based on matching Alt tag
   */
  #handleBackgroundColorChange = (event) => {
    const detail = event.detail || {};

    if (!detail.sectionId || (detail.sectionId !== this.sectionId && detail.sectionId !== 'default')) {
      return;
    }

    // Fallback to scoped window storage if detail is missing (common during morphing)
    const bgColor = (detail.bgColor || window[`MediaGallery_lastFilterLabel_${this.sectionId}`] || '').trim();
    const isWithoutFrame = detail.isWithoutFrame !== undefined ? detail.isWithoutFrame : (window[`MediaGallery_lastIsWithoutFrame_${this.sectionId}`] || false);
    const frameColor = (detail.frameColor !== undefined ? detail.frameColor : (window[`MediaGallery_lastFrameColor_${this.sectionId}`] || '')).trim();
    
    const sector = this.closest('featured-product-information[id], .shopify-section');

    // Get Body Type from detail or DOM (Scoped to local section)
    const getOptionValue = (name) => {
      const root = sector || document;
      const input = root.querySelector(`fieldset[data-option-name="${name}"] input:checked, fieldset[data-option-name="${name.replace(/\s+/g, '-')}"] input:checked`);
      return input ? input.value : '';
    };
    const bodyType = (detail.bodyType !== undefined ? detail.bodyType : (window[`MediaGallery_lastBodyType_${this.sectionId}`] || getOptionValue('body type') || '')).trim();

    // Get all currently selected variant values for generic matching (Scoped to local section)
    const getActiveVariants = () => {
      const variants = [];
      const root = sector || document;
      root.querySelectorAll('variant-picker fieldset.variant-option input:checked, variant-picker select, variant-picker input[type="hidden"].custom-single-option-input').forEach(el => {
        if (el.value) variants.push(el.value.trim());
      });
      return variants;
    };
    const activeVariants = getActiveVariants();

    // Persist for next gallery instance
    this.#lastFilterLabel = bgColor;
    window[`MediaGallery_lastIsWithoutFrame_${this.sectionId}`] = isWithoutFrame;
    window[`MediaGallery_lastFrameColor_${this.sectionId}`] = frameColor;
    window[`MediaGallery_lastBodyType_${this.sectionId}`] = bodyType;
    window[`MediaGallery_lastActiveVariants_${this.sectionId}`] = activeVariants;

    // Normalization helper for more robust matching (handles spaces, dashes, case, etc.)
    const normalize = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '');
    
    // Main patterns: [bg style] - [frame label] AND [bg style] - [body type label]
    const targetPattern = isWithoutFrame
        ? `${bgColor} - No Frame`.toLowerCase().trim()
        : `${bgColor} - ${frameColor}`.toLowerCase().trim();
    
    const bodyTypePattern = `${bgColor} - ${bodyType}`.toLowerCase().trim();

    const normalizedTarget = normalize(targetPattern);
    const normalizedBodyTypePattern = normalize(bodyTypePattern);
    const normalizedBg = normalize(bgColor);
    const normalizedBgStylePrefix = normalize('bg style - ');

    // Main gallery filtering (grids, slides, dialog thumbnails)
    const mediaItems = Array.from(this.querySelectorAll('[data-media-alt]'));
    let visibleCount = 0;

    const performFilter = (useFallback = false) => {
      visibleCount = 0;
      mediaItems.forEach(item => {
        const alt = item.dataset.mediaAlt || '';
        const normalizedAlt = normalize(alt);
        let match = false;

        if (useFallback) {
          // Fallback: If no color match, show based on framing type divider ("no frame") or if it just matches body type
          const lowerAlt = alt.toLowerCase().trim();
          const isAltNoFrame = lowerAlt.includes('no frame') || lowerAlt.includes('noframe') || lowerAlt.includes('without frame');
          match = isWithoutFrame ? isAltNoFrame : !isAltNoFrame;
        } else {
          // Strict match for: 
          // 1. Full pattern (bg style - frame)
          // 2. Body type pattern (bg style - body type)
          // 3. Just background style
          match = (normalizedAlt === normalizedTarget) || 
                  (normalizedBodyTypePattern && normalizedAlt === normalizedBodyTypePattern) ||
                  (normalizedBg && normalizedAlt === normalizedBg);

          // 4. Support for multi-variant matching: [bg/bg style] - [v1] - [v2] - ...
          if (!match && normalizedBg) {
             const parts = alt.split(' - ').map(p => normalize(p));
             if (parts.length > 1) {
                const firstPart = parts[0];
                if (firstPart === normalizedBg || firstPart === normalizedBgStylePrefix) {
                   const remainingParts = parts.slice(1);
                   const normActive = activeVariants.map(v => normalize(v));
                   
                   // Add framing aliases to catch "No Frame" vs "Without Frame" etc.
                   if (isWithoutFrame) {
                      normActive.push('noframe', 'withoutframe', 'none');
                   }
                   
                   // Check if EVERY part mentioned in the alt tag is among currently selected variants
                   match = remainingParts.every(p => normActive.includes(p));
                }
             }
          }
        }

        if (match) {
          item.style.display = '';
          item.classList.remove('filtered-out');
          visibleCount++;
        } else {
          item.style.display = 'none';
          item.classList.add('filtered-out');
        }
      });
    };

    // Try strict matching first
    performFilter(false);

    // If zero matches, fall back to showing all images for the current mode
    if (visibleCount === 0) {
      performFilter(true);
    }

    let firstVisibleIndex = -1;

    // Check for slideshow thumbnails and synchronize visibility
    const thumbImages = this.querySelectorAll('.slideshow-controls__thumbnail img');
    thumbImages.forEach((img, index) => {
      const alt = img.getAttribute('alt') || '';
      const normalizedThumbAlt = normalize(alt);

      const itemMatch = mediaItems.find(item => {
        const itemAlt = item.dataset.mediaAlt || '';
        const normalizedItemAlt = normalize(itemAlt);
        return normalizedItemAlt === normalizedThumbAlt && item.style.display !== 'none';
      });

      const match = !!itemMatch;

      const thumbBtn = img.closest('button');
      if (thumbBtn) {
        thumbBtn.style.display = match ? '' : 'none';
        if (match && firstVisibleIndex === -1) {
          firstVisibleIndex = index;
        }
      }
    });

    if (firstVisibleIndex === -1 && this.slideshow) {
      const slides = Array.from(this.querySelectorAll('.slideshow__slide'));
      firstVisibleIndex = slides.findIndex(s => s.style.display !== 'none');
    }

    if (this.slideshow && typeof this.slideshow.update === 'function') {
      this.slideshow.update();
    }

    if (firstVisibleIndex !== -1 && this.slideshow && typeof this.slideshow.select === 'function') {
      setTimeout(() => {
        this.slideshow.select(firstVisibleIndex, undefined, { animate: false });

        // Ensure thumbnail active state class or aria-current is updated immediately
        const thumbs = Array.from(this.querySelectorAll('.slideshow-controls__thumbnail'));
        thumbs.forEach((thumb, i) => {
          if (i === firstVisibleIndex) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }, 10);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
